import {
  AiOnboardingCatalogs,
  OnboardingProfileSectionFields,
  SaveOnboardingDetailsFields,
  SoftwareExperiencePercentage,
} from './types';

interface CatalogNormalizationResult {
  fields: SaveOnboardingDetailsFields;
  validationErrors: string[];
}

interface CatalogSectionNormalizationResult {
  fields: OnboardingProfileSectionFields;
  validationErrors: string[];
}

const normalizeCatalogValue = (value: string) =>
  value.toLocaleLowerCase().replace(/[^a-z0-9]/g, '');

const findCatalogOption = <Option>(
  value: string,
  options: Option[],
  getId: (option: Option) => string,
  getLabel: (option: Option) => string,
) => {
  const normalizedValue = normalizeCatalogValue(value);

  return options.find((option) =>
    [getId(option), getLabel(option)].some((candidate) =>
      normalizeCatalogValue(candidate) === normalizedValue));
};

const uniqueValues = (values: string[]) =>
  [...new Set(values)];

const experienceLevelByPercentage: Record<
SoftwareExperiencePercentage,
1 | 2 | 3 | 4 | 5
> = {
  0: 1,
  25: 2,
  50: 3,
  75: 4,
  100: 5,
};

// The onboarding script only asks which software the professional is expert or
// very good at, so any software they did not name is stored as 0%, even when
// the agent omits the remaining percentage.
const defaultRemainingSoftwarePercentage: SoftwareExperiencePercentage = 0;

const getRemainingSoftwareExperienceLevel = (
  remainingSoftwarePercentage: SoftwareExperiencePercentage | null,
) => experienceLevelByPercentage[
  remainingSoftwarePercentage ?? defaultRemainingSoftwarePercentage
];

const normalizeProfessionalCapacity = (
  professionalCapacityId: string,
  catalogs: AiOnboardingCatalogs,
  validationErrors: string[],
) => {
  if (catalogs.professionalCapacityOptions.length === 0) {
    return professionalCapacityId;
  }

  const option = findCatalogOption(
    professionalCapacityId,
    catalogs.professionalCapacityOptions,
    ({ professionalCapacityId }) => professionalCapacityId,
    ({ description }) => description,
  );

  if (!option) {
    validationErrors.push(
      'professionalCapacityId: Please select a listed professional capacity',
    );

    return professionalCapacityId;
  }

  return option.professionalCapacityId;
};

const normalizeContactPreferences = (
  selectedContactPreferences: string[],
  catalogs: AiOnboardingCatalogs,
  validationErrors: string[],
) => {
  if (catalogs.contactPreferenceOptions.length === 0) {
    return selectedContactPreferences;
  }

  return uniqueValues(
    selectedContactPreferences.map((preference) => {
      const option = findCatalogOption(
        preference,
        catalogs.contactPreferenceOptions,
        ({ contactPreferenceId }) => contactPreferenceId,
        ({ name }) => name,
      );

      if (option) {
        return option.contactPreferenceId;
      }

      validationErrors.push(
        'selectedContactPreferences: Please select a listed contact preference',
      );

      return preference;
    }),
  );
};

const normalizeSoftwareExperience = (
  selectedSoftwares: SaveOnboardingDetailsFields['selectedSoftwares'],
  catalogs: AiOnboardingCatalogs,
  validationErrors: string[],
  remainingSoftwarePercentage: SoftwareExperiencePercentage | null,
) => {
  if (catalogs.softwareOptions.length === 0) {
    return selectedSoftwares;
  }

  const selectionsBySoftwareId = new Map<string, 1 | 2 | 3 | 4 | 5>();
  const remainingSoftwareExperienceLevel =
    getRemainingSoftwareExperienceLevel(remainingSoftwarePercentage);

  selectedSoftwares.forEach(({ expLevel, softwareId }) => {
    const option = findCatalogOption(
      softwareId,
      catalogs.softwareOptions,
      (software) => software.softwareId,
      (software) => software.name,
    );

    if (!option) {
      validationErrors.push(
        `selectedSoftwares: ${softwareId} is not a listed software option`,
      );
      return;
    }

    selectionsBySoftwareId.set(option.softwareId, expLevel);
  });

  return catalogs.softwareOptions.map(({ softwareId }) =>
    ({
      expLevel: selectionsBySoftwareId.get(softwareId)
        ?? remainingSoftwareExperienceLevel,
      softwareId,
    }));
};

const normalizeProvince = (
  province: string,
  catalogs: AiOnboardingCatalogs,
  validationErrors: string[],
) => {
  if (catalogs.provinceOptions.length === 0) {
    return province;
  }

  const option = findCatalogOption(
    province,
    catalogs.provinceOptions,
    ({ key }) => key,
    ({ name }) => name,
  );

  if (!option) {
    validationErrors.push('province: Please select a listed province');

    return province;
  }

  return option.key;
};

const normalizeReplacementDuration = (
  preferredReplacementDuration: string,
  catalogs: AiOnboardingCatalogs,
  validationErrors: string[],
) => {
  if (catalogs.preferredReplacementDurationOptions.length === 0) {
    return preferredReplacementDuration;
  }

  const option = catalogs.preferredReplacementDurationOptions.find((value) =>
    normalizeCatalogValue(value)
    === normalizeCatalogValue(preferredReplacementDuration));

  if (!option) {
    validationErrors.push(
      'preferredReplacementDuration: Please select a listed shift length',
    );

    return preferredReplacementDuration;
  }

  return option;
};

export const normalizeOnboardingCatalogFields = (
  fields: SaveOnboardingDetailsFields,
  catalogs: AiOnboardingCatalogs,
  remainingSoftwarePercentage: SoftwareExperiencePercentage | null = null,
): CatalogNormalizationResult => {
  const validationErrors: string[] = [];

  return {
    fields: {
      ...fields,
      professionalCapacityId: normalizeProfessionalCapacity(
        fields.professionalCapacityId,
        catalogs,
        validationErrors,
      ),
      selectedContactPreferences: normalizeContactPreferences(
        fields.selectedContactPreferences,
        catalogs,
        validationErrors,
      ),
      selectedSoftwares: normalizeSoftwareExperience(
        fields.selectedSoftwares,
        catalogs,
        validationErrors,
        remainingSoftwarePercentage,
      ),
      province: normalizeProvince(fields.province, catalogs, validationErrors),
      preferredReplacementDuration: normalizeReplacementDuration(
        fields.preferredReplacementDuration,
        catalogs,
        validationErrors,
      ),
    },
    validationErrors,
  };
};

// A profile section carries only some of the catalog-backed fields, so only the
// ones it contains are normalized.
export const normalizeOnboardingCatalogSectionFields = (
  fields: OnboardingProfileSectionFields,
  catalogs: AiOnboardingCatalogs,
  remainingSoftwarePercentage: SoftwareExperiencePercentage | null = null,
): CatalogSectionNormalizationResult => {
  const validationErrors: string[] = [];
  const {
    preferredReplacementDuration,
    professionalCapacityId,
    province,
    selectedContactPreferences,
    selectedSoftwares,
  } = fields;

  return {
    fields: {
      ...fields,
      ...(typeof professionalCapacityId === 'string' && {
        professionalCapacityId: normalizeProfessionalCapacity(
          professionalCapacityId,
          catalogs,
          validationErrors,
        ),
      }),
      ...(Array.isArray(selectedContactPreferences) && {
        selectedContactPreferences: normalizeContactPreferences(
          selectedContactPreferences,
          catalogs,
          validationErrors,
        ),
      }),
      ...(Array.isArray(selectedSoftwares) && {
        selectedSoftwares: normalizeSoftwareExperience(
          selectedSoftwares,
          catalogs,
          validationErrors,
          remainingSoftwarePercentage,
        ),
      }),
      ...(typeof province === 'string' && {
        province: normalizeProvince(province, catalogs, validationErrors),
      }),
      ...(typeof preferredReplacementDuration === 'string' && {
        preferredReplacementDuration: normalizeReplacementDuration(
          preferredReplacementDuration,
          catalogs,
          validationErrors,
        ),
      }),
    },
    validationErrors,
  };
};
